import { useMemo } from 'react';

import { findColumnForCase, WORKFORCE_COLUMNS } from '../data/columns';
import type { CaseItem } from '../data/types';

interface ColumnTotal {
  total: number;
  atRisk: number;
}

export interface GridLayout {
  /** `${assigneeId}::${columnId}` → cases in that grid cell. */
  grouped: Map<string, CaseItem[]>;
  /** Column header rollups — total + at-risk count per workforce column. */
  totals: Record<string, ColumnTotal>;
}

/**
 * Pure grid-layout derivation. Buckets cases by `(assigneeId, column)` and
 * rolls up column header totals in a single pass. `findColumnForCase` is
 * total, so every case lands in exactly one cell and the column totals sum
 * to `cases.length`.
 *
 * Pulled out of the hook's `useMemo` so it can be unit-tested directly. The
 * hook just wraps this with `useMemo` for identity stability across renders.
 */
export function computeGridLayout(cases: CaseItem[]): GridLayout {
  const grouped = new Map<string, CaseItem[]>();
  const totals: Record<string, ColumnTotal> = {};
  for (const col of WORKFORCE_COLUMNS) totals[col.id] = { total: 0, atRisk: 0 };

  for (const c of cases) {
    const col = findColumnForCase({
      state: c.status,
      activeCallId: c.activeCallId,
      humanAuditInProgress: c.humanAuditInProgress,
    });
    const key = `${c.assigneeId}::${col.id}`;
    const arr = grouped.get(key);
    if (arr) arr.push(c);
    else grouped.set(key, [c]);

    totals[col.id].total += 1;
    if (c.riskLevel !== 'healthy') totals[col.id].atRisk += 1;
  }

  return { grouped, totals };
}

export function useGridLayout(cases: CaseItem[]): GridLayout {
  return useMemo(() => computeGridLayout(cases), [cases]);
}
